import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";
import { formatDateForInput, formatDate } from "../utils/utils";

const NUMBER_NFT_PER_PAGE = NumberOfPerPage.onLeave;
const queryKey = "CustomerList";

const initData = {
  data: [],
  totalPages: 0,
  entries: 0,
  startEntry: 0,
  endEntry: 0,
  options: {},
};

const buildMapById = (items = [], labelKey = "name") => {
  const result = {};
  for (const item of items) {
    if (item?._id) {
      result[item._id] = item?.[labelKey] || "";
    }
  }
  return result;
};

const buildAddress = ({
  address = "",
  wardData,
  districtData,
  provinceData,
}) => {
  let result = address || "";

  if (wardData?.name) {
    result += `${result ? ", " : ""}${wardData.name}`;
  }
  if (districtData?.name) {
    result += `${result ? " - " : ""}${districtData.name}`;
  }
  if (provinceData?.name) {
    result += `${result ? " - " : ""}${provinceData.name}`;
  }

  return result || "-";
};

const getGenderLabel = (gender) => {
  if (!gender) return "-";
  if (gender === 1) return "Nam";
  if (gender === 2) return "Nữ";
  return "Khác";
};

const getIdentityLabel = (identityNumber) => {
  if (identityNumber === 2) return "Hộ chiếu";
  return "CCCD/CMND";
};

async function fetchAllData(
  search,
  fetchOptions = {
    job: false,
    employee: false,
    project: false,
    customerType: false,
    saleArea: false,
    saleBlock: false,
  },
) {
  const baseSearchOptions = {
    limit: 100,
    page: 1,
  };

  if (search !== undefined && search !== null && search !== "") {
    baseSearchOptions.search = search;
  }

  const tasks = [];

  if (fetchOptions.job) {
    const params = new URLSearchParams(baseSearchOptions).toString();
    tasks.push(
      clientAPI("get", `/job?${params}`).then((data) =>
        buildMapById(data?.items, "name"),
      ),
    );
  } else {
    tasks.push(Promise.resolve(null));
  }

  if (fetchOptions.employee) {
    const params = new URLSearchParams(baseSearchOptions).toString();
    tasks.push(
      clientAPI("get", `/employee/search?${params}`).then((data) =>
        buildMapById(data?.items, "fullName"),
      ),
    );
  } else {
    tasks.push(Promise.resolve(null));
  }

  if (fetchOptions.project) {
    const params = new URLSearchParams({
      ...baseSearchOptions,
      status: "OPEN",
    }).toString();
    tasks.push(
      clientAPI("get", `/project?${params}`).then((data) =>
        buildMapById(data?.items, "name"),
      ),
    );
  } else {
    tasks.push(Promise.resolve(null));
  }

  if (fetchOptions.customerType) {
    const params = new URLSearchParams({ limit: 100, page: 1 }).toString();
    tasks.push(
      clientAPI("get", `/customerType?${params}`).then((data) =>
        buildMapById(data?.items, "name"),
      ),
    );
  } else {
    tasks.push(Promise.resolve(null));
  }

  if (fetchOptions.saleArea) {
    const params = new URLSearchParams({ limit: 100, page: 1 }).toString();
    tasks.push(
      clientAPI("get", `/saleArea?${params}`).then((data) =>
        buildMapById(data?.items, "name"),
      ),
    );
  } else {
    tasks.push(Promise.resolve(null));
  }

  if (fetchOptions.saleBlock) {
    const params = new URLSearchParams({ limit: 100, page: 1 }).toString();
    tasks.push(
      clientAPI("get", `/saleBlock?${params}`).then((data) =>
        buildMapById(data?.items, "name"),
      ),
    );
  } else {
    tasks.push(Promise.resolve(null));
  }

  const [
    jobList,
    employeeList,
    projectList,
    customerTypeList,
    saleAreaList,
    saleBlockList,
  ] = await Promise.all(tasks);

  return {
    jobList,
    employeeList,
    projectList,
    customerTypeList,
    saleAreaList,
    saleBlockList,
  };
}

export function useConditionalLists(search, fetchOptions) {
  const hasAnyFetch = useMemo(
    () => Object.values(fetchOptions || {}).some(Boolean),
    [fetchOptions],
  );

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["conditionalLists", search, fetchOptions],
    queryFn: () => fetchAllData(search, fetchOptions),
    enabled: hasAnyFetch,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching conditional lists data:", error);
    },
  });

  return {
    jobList: data?.jobList,
    employeeList: data?.employeeList,
    projectList: data?.projectList,
    customerTypeList: data?.customerTypeList,
    saleAreaList: data?.saleAreaList,
    saleBlockList: data?.saleBlockList,
    isLoading,
    isFetching,
    refetch,
  };
}

async function fetchCustomerList(
  currentPage,
  search,
  wishlist,
  employeeId,
  customerTypeId,
  saleBlockId,
  saleAreaId,
  gender,
  type,
) {
  try {
    const options = {};

    if (search !== undefined && search !== null && search !== "") {
      options.search = search;
    }
    if (wishlist?.length > 0) {
      options.wishlist = wishlist;
    }
    if (employeeId) {
      options.employeeId = employeeId;
    }
    if (customerTypeId) {
      options.customerTypeId = customerTypeId;
    }
    if (saleBlockId) {
      options.saleBlockId = saleBlockId;
    }
    if (saleAreaId) {
      options.saleAreaId = saleAreaId;
    }
    if (gender) {
      options.gender = gender;
    }
    if (type) {
      options.type = type;
    }

    const queryParams = new URLSearchParams({
      ...options,
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    }).toString();

    const data = await clientAPI("get", `/customer/v2?${queryParams}`);
    const total = data?.pagination?.total || 0;
    const page = data?.pagination?.page || 1;
    const limit = data?.pagination?.limit || NUMBER_NFT_PER_PAGE;

    const totalPages = Math.ceil(total / NUMBER_NFT_PER_PAGE);
    const entries = total;
    const startEntry = total > 0 ? (page - 1) * limit + 1 : 0;
    const endEntry = Math.min(page * limit, entries);

    const newData = (data?.items || []).map((item) => {
      const genderData = getGenderLabel(item?.gender);
      const identityData = getIdentityLabel(item?.identityNumber);

      const permanentAddress = buildAddress({
        address: item?.permanentAddress,
        wardData: item?.permanentAddressWardData,
        districtData: item?.permanentAddressDistrictData,
        provinceData: item?.permanentAddressProvinceData,
      });

      const currentAddress = buildAddress({
        address: item?.currentAddress,
        wardData: item?.currentAddressWardData,
        districtData: item?.currentAddressDistrictData,
        provinceData: item?.currentAddressProvinceData,
      });

      return {
        name: item?.name || "-",
        gender: !item?.isBusiness ? genderData : "-",
        isBusiness: item?.isBusiness ? 1 : 0,
        type: item?.customerType?.name || "-",
        project: item?.wishlistDetail?.map((project) => project?.name) || [],
        phone: item?.phone || "-",
        email: item?.email || "-",
        businessTaxCode: item?.businessTaxCode || "-",
        identityNumber: identityData,
        identificationNumber: item?.identificationNumber || "-",
        employee: item?.employee?.fullName || "-",
        saleBlock: item?.employee?.saleBlock?.name || "-",
        saleArea: item?.employee?.saleArea?.name || "-",
        identificationInfo: {
          career: item?.job?.name || "-",
          permanentAddress,
          currentAddress,
        },
        idCardFrontImage: item?.idCardFrontImage || "",
        idCardBackImage: item?.idCardBackImage || "",
        vnidImage: item?.vnidImage || "",
        identificationInfoBusiness: {
          businessRegistrationPlace: item?.businessRegistrationPlace || "-",
          representativeName: item?.representativeName || "-",
          representativeGender: item?.isBusiness ? genderData : "-",
          representativePosition: item?.representativePosition || "-",
          representativeIdNumber: item?.representativeIdNumber || "-",
        },
        idRepresentativeCardFrontImage:
          item?.idRepresentativeCardFrontImage || "",
        idRepresentativeCardBackImage:
          item?.idRepresentativeCardBackImage || "",
        representativeVnidImage: item?.representativeVnidImage || "",
        businessRegistrationImage: item?.businessRegistrationImage || [],
        bankAccount: item?.bankAccount || "-",
        bankName: item?.bankName || "-",
        nameAccount: item?.nameAccount || "-",
        isNewDivision: !!item?.isNewDivision,
        edit: {
          ...(item?._id && { id: item._id }),
          ...(item?.name && { name: item.name }),
          ...(item?.gender && { gender: item.gender }),
          ...(item?.phone && { phone: item.phone }),
          ...(item?.email && { email: item.email }),
          ...(item?.identificationNumber && {
            identificationNumber: item.identificationNumber,
          }),
          ...(item?.dateOfIdentification && {
            dateOfIdentification: formatDateForInput(
              formatDate(item.dateOfIdentification),
            ),
          }),
          ...(item?.dateOfBirth && {
            dateOfBirth: formatDateForInput(formatDate(item.dateOfBirth)),
          }),
          ...(item?.placeOfIdentification && {
            placeOfIdentification: item.placeOfIdentification,
          }),
          ...(item?.permanentAddress && {
            permanentAddress: item.permanentAddress,
          }),
          ...(item?.permanentAddressProvince && {
            permanentAddressProvince: item.permanentAddressProvince,
          }),
          ...(item?.permanentAddressDistrict && {
            permanentAddressDistrict: item.permanentAddressDistrict,
          }),
          ...(item?.permanentAddressWard && {
            permanentAddressWard: item.permanentAddressWard,
          }),
          ...(item?.currentAddress && { currentAddress: item.currentAddress }),
          ...(item?.currentAddressDistrict && {
            currentAddressDistrict: item.currentAddressDistrict,
          }),
          ...(item?.currentAddressProvince && {
            currentAddressProvince: item.currentAddressProvince,
          }),
          ...(item?.currentAddressWard && {
            currentAddressWard: item.currentAddressWard,
          }),
          ...(item?.jobId && { jobId: item.jobId }),
          ...(item?.customerTypeId && { customerTypeId: item.customerTypeId }),
          ...(item?.wishlist && { wishlist: item.wishlist }),
          ...(item?.bankAccount && { bankAccount: item.bankAccount }),
          ...(item?.bankName && { bankName: item.bankName }),
          ...(item?.nameAccount && { nameAccount: item.nameAccount }),
          isBusiness: item?.isBusiness || false,
          ...(item?.businessTaxCode && {
            businessTaxCode: item.businessTaxCode,
          }),
          ...(item?.businessRegistrationPlace && {
            businessRegistrationPlace: item.businessRegistrationPlace,
          }),
          businessAddress: item?.businessAddress || "",
          businessLocationAddress: item?.businessLocationAddress || "",
          businessRegistrationDate: item?.businessRegistrationDate
            ? formatDateForInput(formatDate(item.businessRegistrationDate))
            : "",
          ...(item?.representativeName && {
            representativeName: item.representativeName,
          }),
          ...(item?.representativeIdNumber && {
            representativeIdNumber: item.representativeIdNumber,
          }),
          ...(item?.representativeIdIssuedDate && {
            representativeIdIssuedDate: formatDateForInput(
              formatDate(item.representativeIdIssuedDate),
            ),
          }),
          ...(item?.representativeIdIssuedPlace && {
            representativeIdIssuedPlace: item.representativeIdIssuedPlace,
          }),
          ...(item?.representativePosition && {
            representativePosition: item.representativePosition,
          }),
          ...(item?.employeeId && { employeeId: item.employeeId }),
          idCardFrontImage: item?.idCardFrontImage || "",
          idCardBackImage: item?.idCardBackImage || "",
          vnidImage: item?.vnidImage || "",
          idRepresentativeCardFrontImage:
            item?.idRepresentativeCardFrontImage || "",
          idRepresentativeCardBackImage:
            item?.idRepresentativeCardBackImage || "",
          representativeVnidImage: item?.representativeVnidImage || "",
          businessRegistrationImage: item?.businessRegistrationImage || [],
          customer_shares: item?.customer_shares || [],
          isNewDivision: !!item?.isNewDivision,
          identityNumber: item?.identityNumber || 1,
        },
        detail: {
          ...(item?._id && { id: item._id }),
          ...(item?.name && { name: item.name }),
          ...(item?.gender && { gender: genderData }),
          ...(item?.phone && { phone: item.phone }),
          ...(item?.email && { email: item.email }),
          identityNumber: identityData,
          ...(item?.identificationNumber && {
            identificationNumber: item.identificationNumber,
          }),
          ...(item?.dateOfIdentification && {
            dateOfIdentification: item.dateOfIdentification,
          }),
          ...(item?.dateOfBirth && {
            dateOfBirth: item.dateOfBirth,
          }),
          ...(item?.permanentAddress && {
            permanentAddress: item.permanentAddress,
          }),
          ...(item?.permanentAddressProvinceData && {
            permanentAddressProvinceData: item.permanentAddressProvinceData,
          }),
          ...(item?.permanentAddressDistrictData && {
            permanentAddressDistrictData: item.permanentAddressDistrictData,
          }),
          ...(item?.permanentAddressWardData && {
            permanentAddressWardData: item.permanentAddressWardData,
          }),
          ...(item?.currentAddress && { currentAddress: item.currentAddress }),
          ...(item?.currentAddressDistrictData && {
            currentAddressDistrictData: item.currentAddressDistrictData,
          }),
          ...(item?.currentAddressProvinceData && {
            currentAddressProvinceData: item.currentAddressProvinceData,
          }),
          ...(item?.currentAddressWardData && {
            currentAddressWardData: item.currentAddressWardData,
          }),
          ...(item?.job && { job: item?.job?.name }),
          ...(item?.customerType && {
            customerType: item?.customerType?.name,
          }),
          ...(item?.wishlistDetail && { wishlistDetail: item.wishlistDetail }),
          ...(item?.bankAccount && { bankAccount: item.bankAccount }),
          ...(item?.bankName && { bankName: item.bankName }),
          ...(item?.nameAccount && { nameAccount: item.nameAccount }),
          isBusiness: item?.isBusiness || false,
          ...(item?.businessTaxCode && {
            businessTaxCode: item.businessTaxCode,
          }),
          ...(item?.businessRegistrationPlace && {
            businessRegistrationPlace: item.businessRegistrationPlace,
          }),
          businessAddress: item?.businessAddress || "",
          businessLocationAddress: item?.businessLocationAddress || "",
          businessRegistrationDate: item?.businessRegistrationDate || "",
          ...(item?.representativeName && {
            representativeName: item.representativeName,
          }),
          ...(item?.representativeIdNumber && {
            representativeIdNumber: item.representativeIdNumber,
          }),
          ...(item?.representativeIdIssuedDate && {
            representativeIdIssuedDate: item.representativeIdIssuedDate,
          }),
          ...(item?.representativeIdIssuedPlace && {
            representativeIdIssuedPlace: item.representativeIdIssuedPlace,
          }),
          ...(item?.representativePosition && {
            representativePosition: item.representativePosition,
          }),
          ...(item?.placeOfIdentification && {
            placeOfIdentification: item.placeOfIdentification,
          }),
          idCardFrontImage: item?.idCardFrontImage,
          idCardBackImage: item?.idCardBackImage,
          vnidImage: item?.vnidImage,
          idRepresentativeCardFrontImage: item?.idRepresentativeCardFrontImage,
          idRepresentativeCardBackImage: item?.idRepresentativeCardBackImage,
          representativeVnidImage: item?.representativeVnidImage,
          businessRegistrationImage: item?.businessRegistrationImage || [],
          employee: item?.employee?.fullName || "",
          saleBlock: item?.employee?.saleBlock?.name || "",
          saleArea: item?.employee?.saleArea?.name || "",
          customer_shares: item?.customer_shares || [],
          isNewDivision: !!item?.isNewDivision,
        },
      };
    });

    return {
      data: newData,
      totalPages,
      entries,
      startEntry,
      endEntry,
      options,
    };
  } catch (error) {
    console.error("Error fetching customer data:", error);
    return initData;
  }
}

export function useCustomerList(
  search,
  wishlist,
  employeeId,
  customerTypeId,
  saleBlockId,
  saleAreaId,
  gender,
  type,
) {
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    wishlist,
    employeeId,
    customerTypeId,
    saleBlockId,
    saleAreaId,
    gender,
    type,
  ]);

  const { data, refetch, isLoading, isRefetching, isFetching } = useQuery({
    queryKey: [
      queryKey,
      currentPage,
      search,
      wishlist,
      employeeId,
      customerTypeId,
      saleBlockId,
      saleAreaId,
      gender,
      type,
    ],
    queryFn: () =>
      fetchCustomerList(
        currentPage,
        search,
        wishlist,
        employeeId,
        customerTypeId,
        saleBlockId,
        saleAreaId,
        gender,
        type,
      ),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching customer list data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < (data?.totalPages || 0)) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return {
    optionsData: data?.options || {},
    customerListData: data?.data || initData.data,
    totalPages: data?.totalPages || initData.totalPages,
    refetch,
    isLoading,
    isRefetching: isRefetching || isFetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries || initData.entries,
    startEntry: data?.startEntry || initData.startEntry,
    endEntry: data?.endEntry || initData.endEntry,
  };
}